import { useCallback, useContext } from 'react';
import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';
import { RESOURCE_TYPE_DATA_SET, RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import useEntities from 'new/state/entities/repository/useEntities';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';

interface UseNodeCreationActionsProps {
  /** The node/item data (optional if using dynamic item) */
  item?: any;
  /** Pipeline ID for refresh (defaults to item.origin_node_id) */
  pipelineId?: number;
  /** Optional callback before action is dispatched */
  onBeforeAction?: () => void;
  /** Optional callback after action is dispatched */
  onAfterAction?: () => void;
}

/**
 * Hook that provides Transform and Connector creation actions.
 * Shared between drag-to-connect (FlowNodes) and click-to-connect (TerminatingHandle).
 *
 * Usage:
 * - With static item: useNodeCreationActions({ item })
 * - With dynamic item: useNodeCreationActions({}).createTransform(item)
 */
export function useNodeCreationActions({
  item: staticItem,
  pipelineId,
  onBeforeAction,
  onAfterAction,
}: UseNodeCreationActionsProps = {}) {
  const { setAction } = useContext(ResourceEditContext) || {};
  const { data: entities } = useEntities();
  const { refreshPipelineById } = usePipelinesAdapter();

  const createTransform = useCallback(
    (dynamicItem?: any) => {
      const item = dynamicItem || staticItem;
      if (!setAction || !item) return;

      onBeforeAction?.();

      const mergedSourceData = getResourceEntity(item, entities);
      const refreshId = pipelineId ?? Number(item.origin_node_id);

      setAction({
        resource: RESOURCE_TYPE_DATA_SET,
        type: 'create',
        id: mergedSourceData?.id,
        onComplete: () => {
          refreshPipelineById(refreshId);
        },
      });

      onAfterAction?.();
    },
    [
      setAction,
      staticItem,
      entities,
      refreshPipelineById,
      pipelineId,
      onBeforeAction,
      onAfterAction,
    ],
  );

  const createTarget = useCallback(
    (dynamicItem?: any) => {
      const item = dynamicItem || staticItem;
      if (!setAction || !item) return;

      onBeforeAction?.();

      const mergedSourceData = getResourceEntity(item, entities);
      setAction({
        resource: RESOURCE_TYPE_SOURCE,
        type: 'create',
        flowType: 'api_server',
        parentDataSetId: mergedSourceData?.id,
      });

      onAfterAction?.();
    },
    [setAction, staticItem, entities, onBeforeAction, onAfterAction],
  );

  // Split opens a dialog rather than dispatching a create action, so the hook
  // only resolves the parent Nexset the dialog needs; the caller owns the dialog.
  const getSplitParentDataSet = useCallback(
    (dynamicItem?: any) => {
      const item = dynamicItem || staticItem;
      if (!item) return null;

      return getResourceEntity(item, entities);
    },
    [staticItem, entities],
  );

  // Convenience wrappers when using static item
  const handleTransform = useCallback(() => createTransform(), [createTransform]);
  const handleTarget = useCallback(() => createTarget(), [createTarget]);

  return {
    // For static item usage (ApiServerNode)
    handleTransform,
    handleTarget,
    // For dynamic item usage (FlowNodes)
    createTransform,
    createTarget,
    // Shared by both: resolves the parent Nexset for the split dialog
    getSplitParentDataSet,
  };
}
